import React from 'react';
import { Box, Card, CardContent, CardActionArea, Grid, Chip, Typography, Paper, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useStaff } from '../contexts/StaffContext';
import { hasPermission, StaffPermission } from '../utils/rolePermissions';
import {
  Groups,
  Description,
  Quiz,
  Assignment,
  Grading,
  Event,
  Payment,
  Subject,
  Assessment
} from '@mui/icons-material';

interface DashboardCard {
  label: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  color: string;
  permission?: StaffPermission;
}

const dashboardCards: DashboardCard[] = [
  {
    label: 'Students',
    description: 'Manage your class students and view their information',
    path: '/staff/students',
    icon: <Groups sx={{ fontSize: 48, color: '#1976d2' }} />,
    color: '#e3f2fd',
    permission: 'manage_students'
  },
  {
    label: 'Term History',
    description: 'Review previous term submissions, eligibility, and class performance.',
    path: '/staff/term-history',
    icon: <Assessment sx={{ fontSize: 48, color: '#0288d1' }} />,
    color: '#e1f5fe',
    permission: 'manage_students'
  },
  {
    label: 'Subjects',
    description: 'Create and manage subjects available for your classes',
    path: '/staff/subjects',
    icon: <Subject sx={{ fontSize: 48, color: '#5e35b1' }} />,
    color: '#ede7f6',
    permission: 'manage_students'
  },
  {
    label: 'Materials',
    description: 'Upload and manage subject study materials for your classes',
    path: '/staff/materials',
    icon: <Description sx={{ fontSize: 48, color: '#388e3c' }} />,
    color: '#e8f5e9',
    permission: 'manage_students'
  },
  {
    label: 'Quizzes',
    description: 'Create and manage quizzes for student assessment',
    path: '/staff/quizzes',
    icon: <Quiz sx={{ fontSize: 48, color: '#d32f2f' }} />,
    color: '#ffebee',
    permission: 'manage_students'
  },
  {
    label: 'Assignments',
    description: 'Create and assign homework and classwork to students',
    path: '/staff/assignments',
    icon: <Assignment sx={{ fontSize: 48, color: '#7b1fa2' }} />,
    color: '#f3e5f5',
    permission: 'manage_students'
  },
  {
    label: 'Grading',
    description: 'Grade and provide feedback on student submissions',
    path: '/staff/grading',
    icon: <Grading sx={{ fontSize: 48, color: '#f57f17' }} />,
    color: '#fff3e0',
    permission: 'manage_students'
  },
  {
    label: 'Attendance',
    description: 'Track and manage daily student attendance records',
    path: '/staff/attendance',
    icon: <Event sx={{ fontSize: 48, color: '#00796b' }} />,
    color: '#e0f2f1',
    permission: 'manage_students'
  },
  {
    label: 'Payments',
    description: 'View payment records and financial information',
    path: '/staff/payments',
    icon: <Payment sx={{ fontSize: 48, color: '#455a64' }} />,
    color: '#eceff1',
    permission: 'view_payments'
  },
  {
    label: 'Student Quiz Scores',
    description: 'View quiz scores submitted by your students',
    path: '/staff/quiz-scores',
    icon: <Assessment sx={{ fontSize: 48, color: '#0288d1' }} />,
    color: '#e1f5fe',
    permission: 'manage_students'
  }
];

const StaffDashboard: React.FC = () => {
  const { staff } = useStaff();
  const navigate = useNavigate();

  // Filter cards based on permissions
  const visibleCards = dashboardCards.filter(
    (card) => !card.permission || (staff && hasPermission(staff.role, card.permission))
  );

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'primary.contrastText' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Welcome, {staff?.name || 'Staff Member'}! 👋
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are logged in as a <strong>{staff?.role}</strong>. Access your tools and resources below.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 0.5 }}>
              Role
            </Typography>
            <Chip
              label={staff?.role || 'Unknown'}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.3)',
                color: 'primary.contrastText',
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Permissions Overview */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Active Permissions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {staff?.permissions
            ? Object.entries(staff.permissions)
                .filter(([, enabled]) => enabled)
                .map(([permission]) => (
                  <Chip
                    key={permission}
                    label={permission.replace(/_/g, ' ')}
                    color="success"
                    variant="outlined"
                  />
                ))
            : 'No permission data available.'}
        </Box>
      </Paper>

      {/* Dashboard Cards Grid */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#333' }}>
          Quick Access to Tools
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {visibleCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.path}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: card.color,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                  borderColor: '#bbb'
                }
              }}
            >
              <CardActionArea
                onClick={() => handleCardClick(card.path)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}
              >
                <CardContent sx={{ width: '100%', textAlign: 'center', pt: 3 }}>
                  {/* Icon */}
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    {card.icon}
                  </Box>

                  {/* Title */}
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a1a1a' }}>
                    {card.label}
                  </Typography>

                  {/* Description */}
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5, minHeight: '2.8em' }}>
                    {card.description}
                  </Typography>

                  {/* Action Text */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'inline-block',
                      mt: 2,
                      px: 2,
                      py: 1,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderRadius: 1,
                      fontWeight: 'bold',
                      color: '#666',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    Open →
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Help Text */}
      <Paper sx={{ p: 3, mt: 4, backgroundColor: '#f5f5f5' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>💡 Tip:</strong> Click any card above to access the feature. The menu on the left is always available for quick navigation.
        </Typography>
      </Paper>
    </Container>
  );
};

export default StaffDashboard;
